<template>
    <div v-if="paymentFailed" class="sub-alert sub-alert--error" v-ripple="paymentsEnabled"
        @click="goToChangePaymentMethod">
        <span class="sub-alert__icon">
            <v-icon size="18" color="error">mdi-credit-card-sync-outline</v-icon>
        </span>
        <div class="sub-alert__body">
            <div class="sub-alert__title">{{ title }}</div>
            <div class="sub-alert__hint" v-if="nextIntentDate">{{ nextIntentDate }}</div>
            <div class="sub-alert__hint">{{ $t('payment_failed_messages.contact_with_bank') }}</div>
            <div class="sub-alert__hint sub-alert__hint--strong" v-if="accountDisabled">{{ accountDisabled }}</div>
            <div class="sub-alert__hint" v-if="!paymentsEnabled">{{ $t('payment.change_plan_from_desktop') }}</div>
        </div>
    </div>
    <div v-else-if="isFremiumPlan" class="sub-alert sub-alert--secondary sub-alert--stack">
        <div class="sub-alert__text">{{ $t('payment.freemium_end_title') }}</div>
        <div class="sub-alert__actions">
            <v-btn small outlined color="secondary" class="sub-alert__btn" @click="showFreemiumEndDialog">
                {{ $t('common.more_information') }}
            </v-btn>
            <v-btn v-if="showRedirect" small depressed color="primary" class="sub-alert__btn black--text" @click="goToChange">
                {{ changeLabelButton }}
            </v-btn>
        </div>
        <freemium-end-dialog ref="freemiumEnd" />
    </div>
</template>

<script>
import FreemiumEndDialog from '../common/FreemiumEndDialog.vue'

export default {
  components: { FreemiumEndDialog },
    name: "SubscriptionStatus",
    computed: {
        currentSubscription(vm = this) {
            return vm.account?.stripeAccount?.currentSubscription || null
        },
        paymentFailed(vm = this) {
            if (!vm.currentSubscription) return false
            return Boolean(vm.currentSubscription.paymentFailed)
        },
        isFremiumPlan(vm = this) {
            return vm.currentSubscription?.priceId === 'price_1KlzRsEO5hTr6qAY4WRvQizn'
        },
        changePaymentRoute(vm = this) {
            return `${vm.accountType}.payment.edit`
        },
        changePlanRoute(vm = this) {
            return `${vm.accountType}.select-plan`
        },
        showRedirect(vm = this) {
            if (!vm.paymentsEnabled) return false
            return vm.$route.name !== vm.changePaymentRoute && vm.$route.name !== vm.changePlanRoute
        },
        title(vm = this) {
            let defaultMessage = vm.$t('payment_failed_messages.try_again_later')
            let failed = vm.account?.stripeAccount?.failedReason || ''
            if (!failed) return defaultMessage
            return vm.$te(`payment_failed_messages.${failed}`) ?
                vm.$t(`payment_failed_messages.${failed}`) :
                defaultMessage
        },
        nextIntentDate(vm = this) {
            let date = vm.account?.stripeAccount?.currentSubscription?.nextPaymentAttempt || null
            if (!date) return null
            let dateMoment = vm.$moment(date * 1000)
            if (dateMoment.isBefore()) return null
            let dateFormat = dateMoment.format(`D [${vm.$t('common.of')}] MMMM [${vm.$t('common.of_the')}] YYYY`)
            return vm.$t('payment_failed_messages.intent_date', { date: dateFormat })
        },
        accountDisabled(vm = this) {
            let today = vm.$moment();
            let daysUntilNextMonday = (8 - today.isoWeekday()) % 7;
            if (daysUntilNextMonday === 0) daysUntilNextMonday = 7;
            let nextMonday = today.add(daysUntilNextMonday, 'days');
            let formattedDate = nextMonday.format('DD-MM-YYYY');
            return vm.$t('payment_failed_messages.account_disabled', { date: formattedDate })
        },
        changeLabelButton(vm = this) {
            if (vm.account?.stripeAccount?.paymentMethod) return vm.$t('common.change_plan')
            return vm.$t('common.change_card')
        }
    },
    methods: {
        goToChangePaymentMethod() {
            const vm = this
            if (!vm.showRedirect) return;
            vm.$router.push({ name: vm.changePaymentRoute })
        },
        showFreemiumEndDialog() {
            const vm = this;
            vm.$refs.freemiumEnd.show()
        },
        goToChange() {
            const vm = this;
            if (!vm.showRedirect) return;
            if (vm.account?.stripeAccount?.paymentMethod) return vm.$router.push({ name: vm.changePlanRoute })
            vm.$router.push({ name: vm.changePaymentRoute })
        }
    }
}
</script>

<style scoped>
.sub-alert {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px;
    margin-bottom: 12px;
    border-radius: 12px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface-base);
}

.sub-alert--error {
    border-color: rgba(232, 92, 92, .35);
    background: rgba(232, 92, 92, .08);
    cursor: pointer;
    transition: background .15s;
}

.sub-alert--error:hover {
    background: rgba(232, 92, 92, .14);
}

.sub-alert--secondary {
    border-color: rgba(102, 204, 204, .35);
    background: rgba(102, 204, 204, .08);
}

.sub-alert--stack {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
}

.sub-alert__icon {
    flex-shrink: 0;
    display: flex;
    margin-top: 1px;
}

.sub-alert__body {
    flex: 1;
    min-width: 0;
}

.sub-alert__title {
    font-size: var(--fs-body);
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    line-height: 1.4;
}

.sub-alert__hint {
    font-size: var(--fs-caption);
    color: var(--v-txtSecondary-base);
    line-height: 1.4;
    margin-top: 4px;
}

.sub-alert__hint--strong {
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.sub-alert__text {
    font-size: var(--fs-body);
    color: var(--v-txtPrimary-base);
    line-height: 1.45;
    text-align: center;
}

.sub-alert__actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
}

.sub-alert__btn {
    border-radius: 8px !important;
    text-transform: none;
    letter-spacing: normal;
    font-weight: 600;
}
</style>